import React, { useState, useEffect } from 'react';

import { Card } from 'react-bootstrap';

/*
  Product image gallery: a large main image with left/right arrows and a
  row of clickable thumbnails. Accepts an array of image URLs; falls back
  to a single image so it works for products without a gallery yet.
*/
function ProductImageGallery({ images, image, name }) {
  const gallery =
    Array.isArray(images) && images.length > 0
      ? images
      : image
        ? [image]
        : [];

  const [active, setActive] = useState(0);

  /* Reset to first image when the product changes */
  useEffect(() => {
    setActive(0);
  }, [image, images]);

  if (gallery.length === 0) {
    return null;
  }

  const show = (i) => setActive((i + gallery.length) % gallery.length);

  return (
    <Card className="border-0 shadow-lg p-3 pd-img-card">
      <div className="pd-gallery-main">
        <img
          src={gallery[active]}
          alt={name}
          className="pd-main-img"
          onError={(e) => {
            e.currentTarget.src = 'https://placehold.co/600x600?text=No+Image';
          }}
        />

        {gallery.length > 1 && (
          <>
            <button
              type="button"
              className="pd-gallery-arrow pd-gallery-prev"
              onClick={() => show(active - 1)}
              aria-label="Previous image"
            >
              ‹
            </button>
            <button
              type="button"
              className="pd-gallery-arrow pd-gallery-next"
              onClick={() => show(active + 1)}
              aria-label="Next image"
            >
              ›
            </button>
            <span className="pd-gallery-count">
              {active + 1}/{gallery.length}
            </span>
          </>
        )}
      </div>

      {gallery.length > 1 && (
        <div className="pd-gallery-thumbs">
          {gallery.map((src, i) => (
            <button
              type="button"
              key={i}
              className={
                'pd-gallery-thumb ' + (i === active ? 'pd-gallery-thumb-on' : '')
              }
              onClick={() => setActive(i)}
              aria-label={`Image ${i + 1}`}
            >
              <img
                src={src}
                alt={`${name} ${i + 1}`}
                onError={(e) => {
                  e.currentTarget.src =
                    'https://placehold.co/120x120?text=N/A';
                }}
              />
            </button>
          ))}
        </div>
      )}

      <style>{galleryStyles}</style>
    </Card>
  );
}

const galleryStyles = `
  .pd-gallery-main {
    position: relative;
    border-radius: 16px;
    overflow: hidden;
    background: #f8fafc;
  }
  .pd-gallery-main .pd-main-img {
    width: 100%;
    height: clamp(280px, 38vw, 420px);
    object-fit: contain;
    padding: 16px;
    display: block;
  }
  .pd-gallery-arrow {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    width: 40px;
    height: 40px;
    border: none;
    border-radius: 50%;
    background: rgba(255,255,255,0.9);
    color: #1e293b;
    font-size: 1.6rem;
    line-height: 1;
    cursor: pointer;
    box-shadow: 0 4px 12px rgba(0,0,0,0.15);
    transition: background 0.2s ease, transform 0.2s ease;
  }
  .pd-gallery-arrow:hover {
    background: #ea580c;
    color: #fff;
  }
  .pd-gallery-prev { left: 10px; }
  .pd-gallery-next { right: 10px; }
  .pd-gallery-count {
    position: absolute;
    bottom: 10px;
    right: 12px;
    background: rgba(15,23,42,0.7);
    color: #fff;
    font-size: 0.75rem;
    font-weight: 600;
    padding: 3px 10px;
    border-radius: 20px;
  }

  .pd-gallery-thumbs {
    display: flex;
    gap: 8px;
    margin-top: 12px;
    overflow-x: auto;
    padding-bottom: 4px;
  }
  .pd-gallery-thumb {
    flex: 0 0 auto;
    width: 64px;
    height: 64px;
    border: 2px solid #e2e8f0;
    border-radius: 12px;
    overflow: hidden;
    background: #fff;
    padding: 0;
    cursor: pointer;
    transition: border-color 0.2s ease, transform 0.2s ease;
  }
  .pd-gallery-thumb img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
  .pd-gallery-thumb:hover {
    transform: translateY(-2px);
  }
  .pd-gallery-thumb-on {
    border-color: #ea580c;
    box-shadow: 0 4px 10px rgba(234, 88, 12,0.3);
  }
`;

export default ProductImageGallery;
